import { AsyncPipe, NgIf } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import {
  ConfigurationService,
  TimezoneService,
} from '@sc/data-access-configuration';
import { Dossier, Fitter, getOperationType } from '@sc/data-access-models';
import { DossierSandboxService } from '@sc/data-access-state-management';
import { LocaleDatePipe, TranslatorService } from '@sc/data-access-translation';
import {
  FitterNameDisplayService,
  FitterNamePipe,
  PhoneNumberPipe,
} from '@sc/feature-contextual-display';
import { FeatureFlippingManagerService } from '@sc/feature-flipping';
import {
  ButtonComponent,
  IconComponent,
  MomentParseZonePipe,
} from '@sc/ui-components';
import { AppointmentUtils } from '@sc/utils';

@Component({
  selector: 'sc-declaration-end-intervention',
  styleUrls: ['./declaration-end-intervention.component.scss'],
  providers: [FitterNameDisplayService],
  encapsulation: ViewEncapsulation.None,
  imports: [
    NgIf,
    IconComponent,
    ButtonComponent,
    AsyncPipe,
    MomentParseZonePipe,
    TranslateModule,
    LocaleDatePipe,
    FitterNamePipe,
    PhoneNumberPipe,
  ],
  template: `
    <section>
      <h2 class="sc-design-title-h1">
        {{ 'DeclarationEndInformation.interventionTitle' | translate }}
      </h2>

      <aside
        class="_insurer"
        [class.appointment-cancelled]="isAppointmentCancelled()"
        [class.appointment-abandonned]="isDossierAbandonned()"
      >
        <div
          class="intervention"
          *ngIf="!isAppointmentCancelled() && !isDossierAbandonned()"
        >
          <span *ngIf="!isRequestedAppointment()" class="title"
            >{{
              'GlobalApplicativeConstants.time.dateFormat'
                | translate
                  : {
                      dateDay:
                        dossier?.appointment?.time
                        | parseZoneTimeString
                          : timezoneService.getTimezone(dossier?.appointment)
                        | localeDate: 'fullDate'
                        | async,
                      isHalfDay: dossierSB.shouldDisplayAppointmentHalfDay(
                        dossier?.appointment
                      ),
                      dateTime:
                        dossier?.appointment?.time
                        | parseZoneTimeString
                          : timezoneService.getTimezone(dossier?.appointment)
                        | localeDate: 'shortTime'
                        | async,
                      appointmentHalfDay: dossierSB.getTime(
                        dossier?.appointment
                      )
                    }
            }}
          </span>
          <span class="about" *ngIf="!!getOperation()"
            >{{
              'DeclarationEndInformation.sinisterTitle'
                | translate
                  : {
                      operation: getOperation(),
                      sinisterName: getSinisterTitle()
                    }
            }}
          </span>

          <span
            class="replacement-vehicle"
            *ngIf="
              !isOutsideIntervention() &&
              featureFlippingService.isFeatureActivated(
                'can-declare-replacement-vehicle-insurer'
              )
            "
          >
            <sc-icon name="car"></sc-icon>
            <span>{{
              (isReplacementVehicleSelected()
                ? 'DeclarationEndInformation.withReplacementVehicleText'
                : 'DeclarationEndInformation.withoutReplacementVehicleText'
              ) | translate
            }}</span>
          </span>

          <div
            *ngIf="isReplacementVehicleSelected() && isRequestedAppointment()"
            class="request-apmt-vehicle-alert"
          >
            <sc-icon name="warning_circle"></sc-icon>
            <span>{{
              'DeclarationEndInformation.replacementVehicleRequestAlert'
                | translate
            }}</span>
          </div>

          <div class="info-bloc" *ngIf="!!getInterventionAddress()">
            <span class="info-bloc-title">{{
              'DeclarationEndInformation.outsideInterventionAddressTitle'
                | translate
            }}</span>

            <span class="info-bloc-line">
              <sc-icon name="map_location"></sc-icon>
              {{ getInterventionAddress() }}
            </span>
          </div>

          <div *ngIf="fitter" class="info-bloc">
            <span class="info-bloc-title">{{
              'DeclarationEndInformation.fitterInfosTitle' | translate
            }}</span>

            <span class="info-bloc-line">
              <sc-icon name="fitter"></sc-icon>
              {{ fitter | fitterName | async }}
            </span>

            <span *ngIf="displayFitterAddress()" class="info-bloc-line">
              <sc-icon name="map_location"></sc-icon>
              {{ getFitterAddress() }}
            </span>

            <span
              class="info-bloc-line sc-conf-display-fitter-phone"
              *ngIf="fitter?.phone"
            >
              <sc-icon name="phone"></sc-icon>
              {{ fitter.phone | phoneNumber | async }}
            </span>
          </div>
        </div>

        <div class="intervention-cancelled" *ngIf="isAppointmentCancelled()">
          <span class="intervention-cancelled-title">{{
            'DeclarationEndInformation.apmtCancelled' | translate
          }}</span>
          <span class="intervention-cancelled-comment">{{
            dossier?.appointment?.comment ||
              ('DeclarationEndInformation.defaultCancelComment' | translate)
          }}</span>
          <span
            class="info-bloc-line"
            *ngIf="
              this.featureFlippingService.isFeatureActivated(
                'b2b-display-brand-name-declaration-end'
              ) && !!fitter
            "
          >
            <sc-icon name="fitter"></sc-icon>
            {{ fitter?.brand }}
          </span>
        </div>

        <div class="intervention-abandonned" *ngIf="isDossierAbandonned()">
          <span class="intervention-abandonned-title">{{
            'DeclarationEndInformation.dossierAbandonned' | translate
          }}</span>
          <span class="intervention-abandonned-comment">{{
            dossier?.insurerData?.abandonmentComment ||
              ('DeclarationEndInformation.defaultAbandonnementComment'
                | translate)
          }}</span>
        </div>
      </aside>

      <span
        *ngIf="showBtnBloc()"
        class="above-btn-text"
        [innerHTML]="
          'DeclarationEndInformation.appointmentDateNotWanted' | translate
        "
      >
      </span>

      <div *ngIf="showBtnBloc()" class="btn-bloc">
        <sc-button
          *ngIf="canModifyDossier()"
          type="button"
          (click)="onChangeAppointment()"
          >{{
            'DeclarationEndInformation.btnChangeAppointment' | translate
          }}</sc-button
        >
        <sc-button
          *ngIf="canCancelDossier()"
          type="button"
          buttonType="error"
          (click)="onCancelAppointment()"
          >{{
            'DeclarationEndInformation.btnCancelAppointment' | translate
          }}</sc-button
        >
      </div>
    </section>
  `,
})
export class DeclarationEndInterventionComponent {
  @Input() dossier: Dossier;
  @Input() fitter: Fitter;

  @Output() changeAppointment = new EventEmitter<void>();
  @Output() cancelAppointment = new EventEmitter<void>();

  constructor(
    private readonly translator: TranslatorService,
    public featureFlippingService: FeatureFlippingManagerService,
    public timezoneService: TimezoneService,
    public dossierSB: DossierSandboxService,
    private readonly configurationService: ConfigurationService
  ) {}

  canModifyDossier() {
    return (
      this.featureFlippingService.isFeatureActivated(
        'can-do-appointment-modification-insurer'
      ) &&
      this.featureFlippingService.isBrandFeatureActivated(
        this.fitter?.brandId,
        'can-do-appointment-modification-fitter'
      ) &&
      AppointmentUtils.applyModifCutOffFeature(
        this.dossier?.appointment?.time,
        this.configurationService.getConfig().cutOffOptions.modifCutOff
      )
    );
  }

  canCancelDossier() {
    return (
      this.featureFlippingService.isFeatureActivated(
        'can-do-appointment-cancel-insurer'
      ) &&
      this.featureFlippingService.isBrandFeatureActivated(
        this.fitter?.brandId,
        'can-do-appointment-cancel-fitter'
      ) &&
      AppointmentUtils.applyCancelCutOffFeature(
        this.dossier?.appointment?.time,
        this.configurationService.getConfig().cutOffOptions.cancelCutOff
      )
    );
  }

  showBtnBloc() {
    return (
      (this.canModifyDossier() || this.canCancelDossier()) &&
      !this.isAppointmentCancelled() &&
      !this.isDossierAbandonned() &&
      !this.isRequestedAppointment()
    );
  }

  // Only show client file mode if no real appointment has been booked, because we keep requestAppointment even after that
  isRequestedAppointment() {
    return !!this.dossier?.requestAppointment && !this.dossier?.appointment;
  }

  isAppointmentCancelled() {
    return (
      (this.dossier?.appointment?.status === 'CANCELLED' ||
        (!this.dossier?.appointment &&
          this.dossier?.requestAppointment?.status === 'CANCELLED')) &&
      !this.isDossierAbandonned()
    );
  }

  isDossierAbandonned() {
    return this.dossier?.state === 'ABANDONED';
  }

  isOutsideIntervention() {
    return (
      this.dossier?.appointment?.outsideIntervention ??
      this.dossier?.requestAppointment?.outsideIntervention
    );
  }

  isReplacementVehicleSelected() {
    return (
      this.dossier?.appointment?.isReplacementVehicleSelected ??
      this.dossier?.requestAppointment?.isReplacementVehicleSelected
    );
  }

  displayFitterAddress() {
    return this.fitter?.fitterType !== 'MOBILE';
  }

  getFitterAddress() {
    return `${this.fitter?.address}, ${this.fitter?.cp} ${this.fitter?.city}`;
  }

  getInterventionAddress() {
    return (
      this.dossier?.appointment?.interventionLocation?.address ||
      this.dossier?.requestAppointment?.interventionLocation?.address ||
      ''
    );
  }

  getSinisterTitle(): string {
    const glass = this.dossier?.claim?.glass;
    if (!glass) {
      return '';
    }
    if (glass.glass === 'sideWindow' && !!glass.lateralPrecisions) {
      return this.translator
        .getInstantTranslation(
          'GlobalApplicativeConstants.sinisterGlass.glass.' +
            glass.glass +
            '.' +
            glass.lateralPrecisions.precision +
            glass.lateralPrecisions.frontConductor
        )
        ?.toLowerCase();
    } else if (glass.glass === 'lights' && !!glass.light) {
      if (!glass.light.lightsEmplacement || !glass.light.lateral) {
        return this.translator
          .getInstantTranslation(
            'GlobalApplicativeConstants.sinisterGlass.glass.' +
              glass.glass +
              '.name'
          )
          ?.toLowerCase();
      }
      return this.translator
        .getInstantTranslation(
          'GlobalApplicativeConstants.sinisterGlass.glass.' +
            glass.glass +
            '.' +
            glass.light.lightsEmplacement +
            glass.light.lateral
        )
        ?.toLowerCase();
    } else if (glass.glass === 'mirror' && !!glass.mirror) {
      return this.translator
        .getInstantTranslation(
          'GlobalApplicativeConstants.sinisterGlass.glass.' +
            glass.glass +
            '.' +
            glass.mirror
        )
        ?.toLowerCase();
    } else
      return this.translator
        .getInstantTranslation(
          'GlobalApplicativeConstants.sinisterGlass.glass.' +
            (glass.glassExtended || glass.glass) +
            '.name'
        )
        ?.toLowerCase();
  }

  getOperation(): string {
    let operationType = this.dossier.claim.operationType;
    if (!operationType) {
      operationType = getOperationType(this.dossier?.claim?.glass);
    }

    if (operationType === 'Repair') {
      return this.translator.getInstantTranslation(
        'GlobalApplicativeConstants.operationType.repairTitle'
      );
    } else if (operationType === 'Replace') {
      return this.translator.getInstantTranslation(
        'GlobalApplicativeConstants.operationType.replaceTitle'
      );
    } else {
      return '';
    }
  }

  onChangeAppointment() {
    this.changeAppointment.emit();
  }

  onCancelAppointment() {
    this.cancelAppointment.emit();
  }
}
